import React, { useState } from 'react';
import { Form, Input, Button, Card, Space, message, Table, Tag, Descriptions, Dropdown, Skeleton, Select } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { PricingResultType } from '../graphql/mutations/pricingQueryMutations';
import { exportToCSV, exportToExcel, exportToJSON } from '../../../../utils/exportUtils';
import { GET_ALL_PRICING_NODE_ENUMS } from '../../../../graphql/queries/settlement/getSettlementCommonQueries';
import { ResourceCode } from '../../../../constants/resourceCodes';
import {HasResource} from "../../../../components/HasResource";

// 添加CSS样式来调整Descriptions标签列宽度
const styles = `
  .pricing-detail-descriptions .ant-descriptions-item-label {
    width: 150px !important;
    min-width: 150px !important;
    max-width: 200px !important;
  }
  .pricing-detail-descriptions .ant-descriptions-item-content {
    width: auto !important;
    min-width: 200px !important;
    white-space: nowrap !important;
    overflow: visible !important;
  }
  .pricing-detail-descriptions .ant-descriptions-item {
    width: 100% !important;
  }
  .pricing-detail-descriptions .ant-descriptions-row {
    width: 100% !important;
  }
`;

// 注入样式
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

/**
 * 格式化 JSON 字符串，保持数字的原始精度
 * 通过直接格式化原始 JSON 字符串，避免 JSON.parse 导致的精度丢失
 */
const formatJsonWithPrecision = (jsonString: string): string => {
  try {
    // 直接格式化原始 JSON 字符串，保持数字的原始精度
    let indent = 0;
    const indentSize = 2;
    const newLine = '\n';
    const indentChar = ' ';
    
    // 添加换行和缩进
    let result = '';
    let inString = false;
    let escapeNext = false;
    let lastChar = '';
    
    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString[i];
      
      if (escapeNext) {
        result += char;
        escapeNext = false;
        lastChar = char;
        continue;
      }
      
      if (char === '\\') {
        result += char;
        escapeNext = true;
        lastChar = char;
        continue;
      }
      
      if (char === '"') {
        result += char;
        inString = !inString;
        lastChar = char;
        continue;
      }
      
      if (inString) {
        result += char;
        lastChar = char;
        continue;
      }
      
      // 跳过字符串外的空白字符
      if (char === ' ' || char === '\n' || char === '\t' || char === '\r') {
        continue;
      }
      
      switch (char) {
        case '{':
        case '[':
          if (result.length > 0 && lastChar !== ',' && lastChar !== '[' && lastChar !== '{') {
            result += newLine + indentChar.repeat(indent * indentSize);
          }
          result += char + newLine + indentChar.repeat((indent + 1) * indentSize);
          indent++;
          lastChar = char;
          break;
        case '}':
        case ']':
          indent--;
          if (lastChar !== '{' && lastChar !== '[' && lastChar !== ',') {
            result += newLine + indentChar.repeat(indent * indentSize);
          } else if (lastChar === '{' || lastChar === '[') {
            // 如果上一个字符是 { 或 [，需要回退缩进
            result = result.slice(0, -((indent + 1) * indentSize + 1)); // 移除多余的缩进和换行
            result += char;
            lastChar = char;
            break;
          }
          result += char;
          lastChar = char;
          break;
        case ',':
          result += char + newLine + indentChar.repeat(indent * indentSize);
          lastChar = char;
          break;
        case ':':
          result += char + ' ';
          lastChar = char;
          break;
        default:
          result += char;
          lastChar = char;
      }
    }
    
    return result;
  } catch (error) {
    // 如果格式化失败，尝试使用标准的 JSON.stringify 方法
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (parseError) {
      // 如果都失败了，返回原始字符串
      console.error('JSON 格式化错误:', error, parseError);
      return jsonString;
    }
  }
};

interface TrackingNumberPricingQueryTabProps {
    onQuery: (trackingNumber: string, pricingNodeCode?: string | null) => void;
    loading: boolean;
    queryResult?: PricingResultType[] | null;
}

const TrackingNumberPricingQueryTab: React.FC<TrackingNumberPricingQueryTabProps> = ({ 
    onQuery, 
    loading, 
    queryResult 
}) => {
    const [form] = Form.useForm();
    const { data: pricingNodeData, loading: pricingNodeLoading } = useQuery(GET_ALL_PRICING_NODE_ENUMS);

    const pricingNodeOptions = pricingNodeData?.getAllPricingNodeEnums?.map((node: { code: string; description: string }) => ({
        value: node.code,
        label: node.description
    })) || [];

    const handleSubmit = (values: any) => {
        const { trackingNumber, pricingNodeCode } = values;
        
        // 去除运单号的空格
        const trimmedTrackingNumber = trackingNumber ? trackingNumber.trim() : '';
        
        if (!trimmedTrackingNumber) {
            message.error('请输入运单号');
            return;
        }

        onQuery(trimmedTrackingNumber, pricingNodeCode || null);
    };

    const handleReset = () => {
        form.resetFields();
    };

    // 导出功能处理函数
    const handleExport = (type: 'csv' | 'excel' | 'json') => {
        if (!queryResult || queryResult.length === 0) {
            message.warning('没有数据可导出');
            return;
        }

        const filename = `运单计费查询_${dayjs().format('YYYY-MM-DD')}`;
        
        try {
            switch (type) {
                case 'csv':
                    exportToCSV(queryResult, filename);
                    message.success('CSV文件导出成功');
                    break;
                case 'excel':
                    exportToExcel(queryResult, filename);
                    message.success('Excel文件导出成功');
                    break;
                case 'json':
                    exportToJSON(queryResult, filename);
                    message.success('JSON文件导出成功');
                    break;
                default:
                    message.error('不支持的导出格式');
            }
        } catch (error) {
            console.error('导出失败:', error);
            message.error('导出失败，请重试');
        }
    };

    // 导出菜单项
    const exportMenuItems: MenuProps['items'] = [
        {
            key: 'csv',
            label: '导出为CSV',
            icon: <DownloadOutlined />,
            onClick: () => handleExport('csv'),
        },
        {
            key: 'excel',
            label: '导出为Excel',
            icon: <DownloadOutlined />,
            onClick: () => handleExport('excel'),
        },
        {
            key: 'json',
            label: '导出为JSON',
            icon: <DownloadOutlined />,
            onClick: () => handleExport('json'),
        },
    ];

    // 获取计费节点描述
    const getPricingNodeDescription = (code: string | null | undefined) => {
        if (!code) return '-';
        const node = pricingNodeOptions.find((option: { value: string; label: string }) => option.value === code);
        return node ? node.label : code;
    };

    // 表格列定义
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: '运单号',
            dataIndex: 'businessId',
            key: 'businessId',
            width: 150,
        },
        {
            title: '外部订单号',
            dataIndex: 'otherBusinessId',
            key: 'otherBusinessId',
            width: 150,
        },
        {
            title: '客户ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 100,
        },
        {
            title: '业务代码',
            dataIndex: 'businessCode',
            key: 'businessCode',
            width: 100,
        },
        {
            title: '业务场景代码',
            dataIndex: 'businessScenarioCode',
            key: 'businessScenarioCode',
            width: 120,
        },
        {
            title: '业务费用项',
            dataIndex: 'businessFeeCode',
            key: 'businessFeeCode',
            width: 120,
        },
        {
            title: '计费节点',
            dataIndex: 'pricingNodeCode',
            key: 'pricingNodeCode',
            width: 120,
            render: (code: string | null | undefined) => getPricingNodeDescription(code),
        },
        {
            title: '计费金额(美分)',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (amount: number) => amount?.toLocaleString() || '0',
        },
        {
            title: '状态',
            dataIndex: 'isSuccess',
            key: 'isSuccess',
            width: 80,
            render: (isSuccess: boolean) => {
                const color = isSuccess ? 'green' : 'red';
                const text = isSuccess ? '计费成功' : '计费失败';
                return <Tag color={color}>{text}</Tag>;
            },
        },
        {
            title: '定价时间',
            dataIndex: 'pricingAt',
            key: 'pricingAt',
            width: 120,
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '业务时间',
            dataIndex: 'businessAt',
            key: 'businessAt',
            width: 120,
            render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
        }
    ];

    // 渲染详细信息卡片
    const renderDetailCard = (pricing: PricingResultType) => (
        <Card key={pricing.id} title={`计费详情 - ${pricing.businessId}`} className="mb-4">
            <Descriptions 
                column={2} 
                bordered 
                size="small"
                className="pricing-detail-descriptions"
            >
                <Descriptions.Item label="ID">{pricing.id}</Descriptions.Item>
                <Descriptions.Item label="运单号">{pricing.businessId}</Descriptions.Item>
                <Descriptions.Item label="外部订单号">{pricing.otherBusinessId || '-'}</Descriptions.Item>
                <Descriptions.Item label="客户ID">{pricing.clientId}</Descriptions.Item>
                <Descriptions.Item label="业务代码">{pricing.businessCode}</Descriptions.Item>
                <Descriptions.Item label="业务场景代码">{pricing.businessScenarioCode}</Descriptions.Item>
                <Descriptions.Item label="业务费用项">{pricing.businessFeeCode}</Descriptions.Item>
                <Descriptions.Item label="计费节点">
                    {getPricingNodeDescription(pricing.pricingNodeCode)}
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                    <Tag color={pricing.isSuccess ? 'green' : 'red'}>
                        {pricing.isSuccess ? '计费成功' : '计费失败'}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="计费金额(美分)">
                    <span className="text-lg font-bold text-red-600">
                        {pricing.amount?.toLocaleString() || '0'}
                    </span>
                </Descriptions.Item>
                <Descriptions.Item label="定价时间">
                    {dayjs(pricing.pricingAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="业务时间">
                    {dayjs(pricing.businessAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                {pricing.billingContentJson && (
                    <Descriptions.Item label="计费内容JSON" span={2}>
                        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
                            {formatJsonWithPrecision(pricing.billingContentJson)}
                        </pre>
                    </Descriptions.Item>
                )}
            </Descriptions>
        </Card>
    );

    return (
        <div className="space-y-6">
            <Card title="根据运单号查询计费信息" className="mt-4">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            label="运单号"
                            name="trackingNumber"
                            rules={[{ required: true, message: '请输入运单号' }]}
                        >
                            <Input
                                placeholder="请输入运单号"
                                size="large"
                            />
                        </Form.Item>
                        <Form.Item
                            label="计费节点（可选）"
                            name="pricingNodeCode"
                        >
                            <Select
                                placeholder="请选择计费节点（可选）"
                                size="large"
                                allowClear
                                loading={pricingNodeLoading}
                                options={pricingNodeOptions}
                            />
                        </Form.Item>
                    </div>

                    <div className="mt-6">
                        <Space>
                            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PRICING_QUERY, ResourceCode.settlement.SETTLEMENT_REVENUE_PRICING_QUERY]}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SearchOutlined />}
                                    loading={loading}
                                    size="large"
                                >
                                    查询计费信息
                                </Button>
                            </HasResource>
                            <Button onClick={handleReset} size="large">
                                重置
                            </Button>
                        </Space>
                    </div>
                </Form>

                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                    <h4 className="text-green-800 font-medium mb-2">查询说明：</h4>
                    <ul className="text-green-700 text-sm space-y-1">
                        <li>• 输入运单号，查询该运单的详细计费信息</li>
                        <li>• 查询结果包含完整的计费明细，包括基础价格、附加费用等</li>
                        <li>• 支持精确查询单个运单的计费数据</li>
                    </ul>
                </div>
            </Card>

            {/* 查询结果 */}
            {loading && (
                <Card title="查询结果">
                    <Skeleton active paragraph={{ rows: 6 }} />
                </Card>
            )}

            {!loading && queryResult && queryResult.length > 0 && (
                <div>
                    <Card 
                        title={
                            <div className="flex justify-between items-center">
                                <span>查询结果 (共 {queryResult.length} 条记录)</span>
                                <Dropdown 
                                    menu={{ items: exportMenuItems }} 
                                    placement="bottomRight"
                                    trigger={['click']}
                                >
                                    <Button 
                                        type="primary" 
                                        icon={<DownloadOutlined />}
                                        size="middle"
                                    >
                                        导出数据 <DownOutlined />
                                    </Button>
                                </Dropdown>
                            </div>
                        }
                    >
                        <Table
                            columns={columns}
                            dataSource={queryResult}
                            rowKey="id"
                            scroll={{ x: 1400 }}
                            pagination={false}
                            size="small"
                         className="mb-4"/>
                    </Card>

                    {/* 详细信息卡片 */}
                    <div className="mt-4">
                        <h3 className="text-lg font-medium mb-4">详细信息</h3>
                        {queryResult.map(renderDetailCard)}
                    </div>
                </div>
            )}

            {queryResult && queryResult.length === 0 && (
                <Card>
                    <div className="text-center py-8 text-gray-500">
                        <SearchOutlined className="text-4xl mb-4" />
                        <p>未找到该运单号的计费记录</p>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default TrackingNumberPricingQueryTab;
